import { STAGE_KEYS, leadStageKey } from './stageKeys';
import type { StageKey } from './stageKeys';
import type { LeadDealOutcome } from './dealOutcomes';
import type { Lead, LeadStage } from './types';

/**
 * Where a lead sits in the pipeline people actually read.
 *
 * ── Why this is not just the stage ─────────────────────────────────────────
 *
 * Kyu, 2026-08-27: the lead pipeline "should not stop at converted to deal.
 * There will be two more steps after that. Won or Loss. And it will only
 * transfer there because it MIRRORS the status of how that deal is going in
 * the actual deal tab... ultimately we also want to measure Lost or Won in the
 * Lead pipeline perspective."
 *
 * The operative word is MIRRORS. A mirror does not hold its own copy of the
 * thing it reflects, so Won and Lost are DERIVED here at read time rather than
 * written onto the lead when a deal closes. Two consequences, both wanted:
 *
 *   - the lead can never disagree with the Deals tab, because there is nothing
 *     to keep in step;
 *   - repeat business survives. A client who bought in 2025 and has an open
 *     deal now would have had the 2025 win overwritten by whatever closed
 *     last. Here the win is still counted, and the lead reads Converted to
 *     Deal because open work outranks history.
 *
 * `stage_id` stays exactly what a human sets — the five keys in stageKeys.ts.
 * Nothing in this module writes anything.
 *
 * ── The stuck case, named rather than hidden ───────────────────────────────
 *
 * Nothing stops somebody setting a lead's status to Converted to Deal from the
 * dropdown without raising a deal. That lead has no outcome to read, so it can
 * never reach Won or Lost. It is the same shape as the legacy rows batch 3a
 * had to backfill — "a customer who looks like they never bought".
 *
 * It is not silently treated as Converted-and-fine. `convertedWithoutDeal`
 * names it so a caller can surface it, and the position stays 'converted'
 * because that IS where somebody put it. Guarding the dropdown is a separate
 * decision and deliberately not made here.
 *
 * ── The mirror no longer waits for Converted to Deal ───────────────────────
 *
 * Until 31 Aug this only derived anything when the lead was already sitting on
 * Converted to Deal. Measured that day: Sheri Breaux's Halo deal was marked
 * Lost, her lead was linked to it, and the Leads tab said Engaged — because
 * nobody had moved her status and the mirror would not start without it.
 *
 * Kyu, asked directly whether a closed deal should pull a lead off Engaged:
 * "Yes." And the gate was redundant anyway — a lead WITH A DEAL has converted,
 * whatever status somebody remembered to set. The Hub was reporting the
 * bookkeeping rather than the business.
 *
 * Two things the widening deliberately does NOT do:
 *
 *   - it never promotes a lead to 'converted'. An open deal leaves the lead on
 *     the status it is on. Deriving Won and Lost is reading an outcome; moving
 *     somebody to Converted is asserting a conversion happened, which is the
 *     dropdown's job and the promote path's job.
 *   - it never overrides 'closed'. That status carries a typed reason, written
 *     to lead history, and a derived Lost would hide it.
 *
 * Still a mirror. Nothing here writes.
 */

/** The five a human sets, plus the two the deals decide. */
export type LeadPosition = StageKey | 'won' | 'lost';

export const POSITION_ORDER: readonly LeadPosition[] = [
  'new',
  'contacted',
  // Follow Up sits after Contacted and before Engaged: emailed, and somebody
  // is still on it. Nurture sits after Engaged and before Converted: they
  // answered, they are interested, not now. Both are positions 25 and 35 in
  // the database for the same reason.
  'followup',
  'engaged',
  'nurture',
  'converted',
  'won',
  'lost',
  'closed',
] as const;

function isStageKey(value: string): value is StageKey {
  return (STAGE_KEYS as readonly string[]).includes(value);
}

/** True when a value names one of the seven positions. */
export function isLeadPosition(value: string): value is LeadPosition {
  return (POSITION_ORDER as readonly string[]).includes(value);
}

/**
 * The position a lead reads at, or null when its status is not in the
 * vocabulary — the same refusal-to-guess rule stageKeys.ts sets.
 *
 * `outcome` undefined means "no deals, or the rollup has not loaded". Both
 * land on the stage as set, which is the honest direction: a Converted lead
 * whose outcomes are still loading reads Converted, not Won.
 */
export function positionOf(
  stageKey: StageKey | null,
  outcome: LeadDealOutcome | undefined,
): LeadPosition | null {
  if (stageKey === null) {
    return null;
  }
  if (outcome === undefined) {
    return stageKey;
  }
  // Open work outranks history, and the lead keeps whatever status it is on.
  // A client with a win behind them and a live deal now reads Converted to
  // Deal, with the win still counted beside it — repeat business is the whole
  // reason open, won and lost are counted separately rather than reduced to
  // one value.
  if (outcome.open_deals > 0) {
    return stageKey;
  }
  // A lead somebody CLOSED, with a typed reason, keeps that answer. Overriding
  // it with a derived Lost would hide the reason, and "who said not right now
  // this year" is a list Kyu asked for by name. Closed is a human decision
  // with a record attached; the rest are just where somebody left the lead.
  if (stageKey === 'closed') {
    return 'closed';
  }
  if (outcome.won_deals > 0) {
    return 'won';
  }
  if (outcome.lost_deals > 0) {
    return 'lost';
  }
  return stageKey;
}

/** The position for a lead row, resolving its stage first. */
export function leadPosition(
  lead: Lead,
  stages: LeadStage[],
  outcomes: ReadonlyMap<string, LeadDealOutcome>,
): LeadPosition | null {
  return positionOf(leadStageKey(lead.stage_id, stages), outcomes.get(lead.id));
}

/**
 * True when the position came from the deals rather than from the status
 * somebody set. The UI uses this to say so, because a value that moved on its
 * own needs to explain itself.
 */
export function positionIsDerived(
  stageKey: StageKey | null,
  position: LeadPosition | null,
): boolean {
  return stageKey !== null && position !== null && position !== stageKey;
}

/**
 * Sitting at Converted to Deal with no deal behind it — set by hand, or a deal
 * deleted afterwards. Cannot progress to Won or Lost because there is nothing
 * to read.
 */
export function convertedWithoutDeal(
  stageKey: StageKey | null,
  outcome: LeadDealOutcome | undefined,
): boolean {
  if (stageKey !== 'converted') {
    return false;
  }
  if (outcome === undefined) {
    return true;
  }
  return outcome.open_deals + outcome.won_deals + outcome.lost_deals === 0;
}

/**
 * What to call a position on screen.
 *
 * The five stages keep whatever the admin renamed them to — code asks for the
 * key, people read the name. Won and Lost are not rows in `lead_stages`, so
 * they are the only two named here.
 */
export function positionLabel(position: LeadPosition | null, stages: LeadStage[]): string {
  if (position === null) {
    return 'Unknown';
  }
  if (position === 'won') {
    return 'Won';
  }
  if (position === 'lost') {
    return 'Lost';
  }
  const stage = stages.find((row) => (row.key ?? '').trim().toLowerCase() === (position as string));
  return stage?.name ?? position;
}

/** The tone a position's badge takes. Only the two derived ones are coloured. */
export function positionTone(position: LeadPosition | null): 'success' | 'neutral' | null {
  if (position === 'won') {
    return 'success';
  }
  if (position === 'lost') {
    return 'neutral';
  }
  return null;
}

/** Counts per position over a set of leads, for the pipeline read-out. */
export function positionCounts(
  leads: Lead[],
  stages: LeadStage[],
  outcomes: ReadonlyMap<string, LeadDealOutcome>,
): Record<LeadPosition, number> {
  const counts = Object.fromEntries(POSITION_ORDER.map((p) => [p, 0])) as Record<
    LeadPosition,
    number
  >;
  for (const lead of leads) {
    const position = leadPosition(lead, stages, outcomes);
    if (position !== null) {
      counts[position] += 1;
    }
  }
  return counts;
}

export { isStageKey };
